import React from 'react';
import PropTypes from 'prop-types';
import DefaultPortrait from '../images/DefaultPortrait.png';

// Components
import InfoPanel from './InfoPanel';
import AlertUpdateWidget from './AlertUpdateWidget';
import LowPowerNotifications from './LowPowerNotifications';
import LineItem from './LineItem';
import InfoDialog from './InfoDialog';

// Constants
import { isNull, lowBatteryPercent } from '../constants';
import { T, FONT_MONO, INDICATOR_TEXT } from '../constants/tokens';
import { locationOf, heartbeatOf } from '../lib/badgeTimes';

const HEADER = (staffMember) => {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <img
        height={48}
        width={48}
        src={staffMember.getImagePath() ?? DefaultPortrait}
        alt=''
        style={{ borderRadius: 9999, objectFit: 'cover', flex: 'none' }}
      />
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 20,
            fontWeight: 600,
            lineHeight: '28px',
            letterSpacing: '-0.4px',
            color: T.ink,
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {staffMember.getName()}
        </div>
        <div
          style={{
            fontFamily: FONT_MONO,
            fontSize: 12,
            fontWeight: 500,
            lineHeight: '16px',
            textTransform: 'uppercase',
            color: T.muted
          }}
        >
          {staffMember?.getStaffType()?.getName()}
        </div>
      </div>
    </div>
  );
};

const CHILDREN = (staffMember) => {
  const badge = staffMember.getBadge();
  const battery = badge?.getBatteryLevel();

  const badgeStatus = badge == null
    ? 'Not assigned'
    : (badge.getIsConnected() ? 'Connected' : 'Not connected');

  return (
    <>
      <LineItem description='Email' value={staffMember.getEmail()} />
      <LineItem description='Phone' value={staffMember.getPhoneNumber()} mono />
      <LineItem description='Badge' value={badge?.getSerialNumber() ?? 'Not assigned'} mono />
      <LineItem
        description='Badge status'
        value={badgeStatus}
        tone={badge != null && !badge.getIsConnected() ? INDICATOR_TEXT.error : null}
      />
      <LineItem
        description='Badge power'
        value={battery == null ? 'Unknown' : Math.floor(battery) + '%'}
        mono
        tone={battery != null && battery < lowBatteryPercent() ? INDICATOR_TEXT.warning : null}
      />
      <LineItem description='Firmware' value={badge?.getFirmwareVersion() && 'v' + badge.getFirmwareVersion()} mono />
      <LineItem description='Assigned room' value={staffMember.getAssignedRoom()?.getIdentifier()} />
      {/* Two clocks, shown apart: where the badge last moved to, and when it
          was last heard from at all. A badge left on a desk keeps the second
          fresh while the first grows old. See lib/badgeTimes. */}
      <LineItem description='Last location' value={locationOf(badge)} />
      <LineItem
        description='Last heartbeat'
        value={heartbeatOf(badge)}
        tone={badge != null && !badge.getIsConnected() ? INDICATOR_TEXT.error : null}
        last
      />

      {/* Shown only while the badge is below the school's low power threshold. */}
      {badge?.getSerialNumber() != null && <LowPowerNotifications serialNumber={badge.getSerialNumber()} />}

      <AlertUpdateWidget updates={staffMember.getAlertUpdates()} />
    </>
  );
};

export default function StaffMemberInfoPanel({ staffMember, isMobile, handleClose }) {
  const statusColor = staffMember.getBadge()?.getLastSeenRoom()?.getAlertStatus()?.getColor()?.main;

  return isMobile ? (
    <InfoDialog
      statusColor={statusColor}
      header={HEADER(staffMember)}
      handleClose={handleClose}
      open={!isNull(staffMember)}
      editHref={'/admin/staff/' + staffMember.getID()}
    >
      {CHILDREN(staffMember)}
    </InfoDialog>
  ) : (
    <InfoPanel
      statusColor={statusColor}
      header={HEADER(staffMember)}
      editHref={'/admin/staff/' + staffMember.getID()}
    >
      {CHILDREN(staffMember)}
    </InfoPanel>
  );
}

StaffMemberInfoPanel.propTypes = {
  staffMember: PropTypes.object,
  isMobile: PropTypes.bool,
  handleClose: PropTypes.func
};
